import React, { ElementType, ReactNode, forwardRef, useMemo, ComponentType } from 'react';
import { Box, BoxProps, SvgIconProps } from '@mui/material';
import { CheckIcon } from '@/src/components/icon/stroke/CheckIcon';
import { CloseIcon } from '@/src/components/icon/stroke/CloseIcon';
import { LoadingText, LoadingTextProps } from './LoadingText';

export interface ProgressStateProps extends BoxProps<ElementType> {
  /**
   * Affects component size
   * @default medium
   */
  size?: 'small' | 'medium' | number;
  /**
   * State to display
   */
  state?: 'error' | 'success';
  /**
   * Message when success appears
   */
  successMessage?: ReactNode;
  /**
   * Message when error appears
   */
  errorMessage?: ReactNode;
  /**
   * Properties applied to message wrapper
   */
  messageProps?: LoadingTextProps;
  /**
   * Overwrites state message (successMessage or errorMessage)
   */
  message?: ReactNode;
  /**
   * Properties applied to icon component
   */
  iconProps?: SvgIconProps;
  /**
   * Overwrites state icon
   */
  IconComponent?: ComponentType<SvgIconProps>;
  /**
   * Properties applied to wrapper of icon
   */
  iconWrapperProps?: BoxProps<ElementType>;
}

/**
 * Component displaying current state of loading
 */
export const ProgressState: React.FC<ProgressStateProps> = forwardRef(
  (
    {
      children,
      sx,
      size = 'medium',
      state,
      message,
      messageProps,
      successMessage,
      errorMessage,
      IconComponent,
      iconProps,
      iconWrapperProps,
      ...rest
    },
    ref,
  ) => {
    const { sx: iconWrapperSx, ...restIconWrapper } = iconWrapperProps ?? {};
    const [IconStateComponent, stateMessage] = useMemo(() => {
      switch (state) {
        case 'error':
          return [CloseIcon, errorMessage];
        case 'success':
        default:
          return [CheckIcon, successMessage];
      }
    }, [state, successMessage, errorMessage]);
    const ProgressStateIcon = IconComponent ?? IconStateComponent;

    return (
      <Box
        data-testid="ProgressMessageWrapper"
        ref={ref}
        sx={{
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1,
          ...sx,
        }}
        {...rest}
      >
        <Box
          key={state}
          sx={{
            '@keyframes flow-progressstate-finished': {
              '0%': {
                transform: 'scale(.8)',
                opacity: '0.8',
              },
              '100%': {
                transform: 'scale(1)',
                opacity: '1',
              },
            },
            animation: 'flow-progressstate-finished 0.4s',
            backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'brand.secondary' : 'brand.primary'),
            ...(state === 'error' && {
              backgroundColor: 'error.main',
            }),
            color: 'common.white',
            borderRadius: '50%',
            lineHeight: '0',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...(size === 'medium' && {
              // 24px
              p: 0.5,
              '& .MuiSvgIcon-root': {
                fontSize: '16px',
              },
            }),
            ...(size === 'small' && {
              // 16px
              p: 0.25,
              '& .MuiSvgIcon-root': {
                fontSize: '12px',
              },
            }),
            ...(typeof size === 'number' && {
              height: `${size}px`,
              width: `${size}px`,
            }),
            ...iconWrapperSx,
          }}
          {...restIconWrapper}
        >
          <ProgressStateIcon data-testid="Icon" {...iconProps} />
        </Box>
        {(children || stateMessage || message) && (
          <LoadingText
            data-testid="ProgressMessage"
            {...(typeof size !== 'number' && { size: size })}
            {...messageProps}
          >
            {children || stateMessage || message}
          </LoadingText>
        )}
      </Box>
    );
  },
);
